<template>
  <section class="tasks-page h-full w-full flex flex-col bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-600 mb-6 flex">
        <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
        <span class="mx-1">/</span>
        <span class="text-blue-900 font-semibold">Task Management</span>
      </nav>

      <!-- Header -->
      <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-6 sm:mb-8">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div class="min-w-0">
            <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
                <path
                  d="M7.5 3.75A2.25 2.25 0 0 0 5.25 6v12A2.25 2.25 0 0 0 7.5 20.25h9A2.25 2.25 0 0 0 18.75 18V7.5a.75.75 0 0 0-.22-.53l-3-3a.75.75 0 0 0-.53-.22H7.5Zm3 6.75a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Zm0 3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Zm-3-3a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 0 1.5H8.25a.75.75 0 0 1-.75-.75Zm0 3a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 0 1.5H8.25a.75.75 0 0 1-.75-.75Z" />
              </svg>
              Task Management
              <div v-if="viewMode === 'tasks' && isTasksLoading" class="ml-2">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
              <div v-if="viewMode === 'boards' && isBoardsLoading" class="ml-2">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            </h1>
            <p class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
              Organize and track your tasks efficiently across boards and workflows.
            </p>
          </div>

          <div class="flex items-center justify-start lg:justify-end gap-3">
            <button @click="setViewMode('tasks')" :class="[
              'px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2',
              viewMode === 'tasks'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span>Tasks</span>
            </button>
            <button @click="setViewMode('boards')" :class="[
              'px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2',
              viewMode === 'boards'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span>Boards</span>
            </button>
            <button @click="setViewMode('charts')" :class="[
              'px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2',
              viewMode === 'charts'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Charts</span>
            </button>
          </div>
        </div>
      </div>

      <!-- TASK LIST VIEW (default) -->
      <template v-if="viewMode === 'tasks'">
        <!-- New Task action bar -->
        <div class="mt-2 mb-4 flex justify-end">
          <button @click="showCreateTaskModal = true"
            class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            <span>New Task</span>
          </button>
        </div>

        <!-- My Tasks / All Tasks / Viewer Tabs -->
        <div class="mb-6">
          <div class="bg-white rounded-2xl border shadow-lg">
            <div class="flex border-b">
              <button @click="setActiveTab('my')" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200',
                activeTab === 'my'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  My Tasks
                </div>
              </button>
              <button @click="setActiveTab('all')" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200',
                activeTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  All Tasks
                </div>
              </button>
              <button @click="setActiveTab('viewer')" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200',
                activeTab === 'viewer'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  Viewer
                </div>
              </button>
            </div>
            <div
              class="px-4 py-3.5 border-t border-gray-100 bg-gradient-to-b from-slate-50/95 to-slate-50 rounded-b-2xl">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-500 shrink-0">Status</span>
                <div class="flex flex-wrap gap-2 items-center justify-start sm:justify-end flex-1 min-w-0">
                  <button v-for="opt in taskStatusOptions" :key="opt.value" type="button"
                    @click="setTaskStatusFilter(opt.value)" :class="[
                      'group inline-flex items-center gap-2 rounded-lg border text-sm font-medium transition-all duration-200 pl-3 pr-2 py-2',
                      taskStatusFilter === opt.value
                        ? 'border-blue-600 bg-blue-600 text-white shadow-sm ring-1 ring-blue-600/20'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    ]">
                    <span class="whitespace-nowrap">{{ opt.label }}</span>
                    <span
                      class="inline-flex min-w-[1.75rem] h-6 items-center justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums leading-none"
                      :class="taskStatusFilter === opt.value
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'">
                      <template v-if="!isTaskStatusCountsLoading">{{ statusCountFor(opt.value) }}</template>
                      <span v-else class="block h-3 w-6 rounded-sm bg-current opacity-25 animate-pulse"
                        aria-hidden="true"></span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Task list search (server-side title / description; same pattern as board view) -->
        <div class="w-full mb-4">
          <div class="w-full bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-4 sm:px-6 sm:py-5">
            <label for="tasks-index-task-search"
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Search tasks</label>
            <div class="relative">
              <svg class="absolute left-4 top-3 w-5 h-5 text-gray-400 pointer-events-none" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input id="tasks-index-task-search" v-model="tasksListSearch" type="search"
                class="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 text-base"
                placeholder="Search by task title or description" autocomplete="off" />
            </div>
            <p v-if="tasksListSearchActiveHint" class="mt-2 text-xs text-gray-500">
              Results update after you pause typing (short delay).
            </p>
          </div>
        </div>

        <!-- Tasks Loading -->
        <div v-if="isTasksLoading" class="space-y-4">
          <div v-for="i in 5" :key="i" class="bg-white rounded-xl shadow-md p-6 animate-pulse">
            <div class="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>

        <!-- Tasks List -->
        <div v-else-if="displayTasks && displayTasks.length > 0" class="space-y-4">
          <div v-for="(task, index) in displayTasks" :key="task.id" @click="openTaskDetail(task.id)"
            class="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-l-4 flex flex-row gap-4 p-5 sm:p-6 sm:gap-5"
            :style="{ borderLeftColor: getPriorityColor(task.priority) }">
            <div class="flex shrink-0 flex-col justify-start pt-0.5 sm:justify-center" aria-hidden="true">
              <span
                class="task-card-index inline-flex h-9 items-center justify-center rounded-full border border-slate-200 bg-gradient-to-b from-white to-slate-50 text-sm font-semibold tabular-nums text-slate-600 shadow-sm ring-1 ring-slate-100">
                {{ index + 1 }}
              </span>
            </div>
            <div class="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-3 flex-wrap">
                  <h3 class="text-lg font-semibold text-gray-900 truncate">{{ task.title }}</h3>
                  <span class="text-xs px-3 py-1 rounded-full text-white shrink-0"
                    :style="{ backgroundColor: getPriorityColor(task.priority) }">
                    {{ task.priority }}
                  </span>
                  <span class="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 shrink-0">
                    {{ getStatusLabel(task.status) }}
                  </span>
                </div>
                <div v-if="task.board && (task.board.master_board || task.board.name)"
                  class="mt-3 flex items-start gap-2.5">
                  <div
                    class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 ring-1 ring-slate-200/80"
                    aria-hidden="true">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-x-1 gap-y-1.5">
                      <template v-if="task.board.master_board && task.board.name">
                        <button type="button" :title="task.board.master_board.name"
                          class="inline-flex max-w-full min-w-0 items-center rounded-lg border border-transparent bg-slate-50 px-2.5 py-1 text-left text-sm font-medium text-slate-600 shadow-sm transition-all duration-200 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-900 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-0"
                          @click.stop="goToTaskMasterBoard(task)">
                          <span class="truncate">{{ task.board.master_board.name }}</span>
                        </button>
                        <svg class="h-4 w-4 shrink-0 text-slate-300" fill="none" stroke="currentColor"
                          viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                        <button type="button" :title="task.board.name"
                          class="inline-flex max-w-full min-w-0 items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-left text-sm font-medium text-slate-700 shadow-sm transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-0"
                          @click.stop="goToTaskSubBoard(task)">
                          <span class="truncate">{{ task.board.name }}</span>
                        </button>
                      </template>
                      <button v-else type="button" :title="task.board.name"
                        class="inline-flex max-w-full min-w-0 items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-left text-sm font-medium text-slate-700 shadow-sm transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-0"
                        @click.stop="goToTaskSubBoard(task)">
                        <span class="truncate">{{ task.board.name }}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="task.assignees && task.assignees.length > 0" class="flex items-center gap-2 mt-2">
                  <span class="text-xs text-gray-500">Assigned to:</span>
                  <span class="text-sm text-gray-700">{{(task.assignees || []).map(a => a.name).join(', ')}}</span>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-2 self-start sm:self-center">
                <span v-if="task.due_date" class="text-sm text-gray-500">{{ formatDate(task.due_date) }}</span>
                <button type="button" title="Open in new window"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-0"
                  @click.stop="openTaskInNewTab(task.id)">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Infinite scroll (scroll target: #dashboard-scroll-container) + list summary -->
          <div v-if="tasksPaginatorInfo" class="mt-6 flex flex-col items-center gap-2">
            <div v-if="tasksLoadingMore" class="text-center py-2 text-sm text-slate-500">Loading more...</div>
            <div v-else-if="!tasksPaginatorInfo.hasMorePages && displayTasks.length > 0"
              class="text-center py-2 text-xs text-slate-400">
              No more tasks
            </div>
            <p class="text-sm text-slate-500 tabular-nums">
              Showing {{ displayTasks.length }} of {{ tasksPaginatorInfo.total }}
              {{ tasksPaginatorInfo.total === 1 ? 'task' : 'tasks' }}
            </p>
          </div>
        </div>

        <!-- Tasks Empty State -->
        <div v-else class="text-center py-16">
          <div class="max-w-md mx-auto">
            <svg class="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2">
              </path>
            </svg>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ emptyTasksListTitle }}</h3>
            <p class="text-gray-600 mb-6">
              {{ emptyTasksListDescription }}
            </p>
            <button @click="showCreateTaskModal = true"
              class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 inline-flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>New Task</span>
            </button>
          </div>
        </div>
      </template>

      <!-- CHARTS VIEW -->
      <template v-else-if="viewMode === 'charts'">
        <tasks-charts />
      </template>

      <!-- BOARDS VIEW -->
      <template v-else>
        <!-- New Master Board / New Sub-Board action bar -->
        <div class="mt-2 mb-4 flex justify-end gap-3">
          <button @click="showCreateMasterBoardModal = true"
            class="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>New Board</span>
          </button>
        </div>

        <!-- Boards Tabs (only for users with view-all-task-management-board) -->
        <div v-if="canViewAllBoards" class="mb-6">
          <div class="bg-white rounded-2xl border shadow-lg">
            <div class="flex border-b">
              <button @click="setBoardsTab('my')" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-l-2xl',
                boardsTab === 'my'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  My Boards
                </div>
              </button>
              <button @click="setBoardsTab('all')" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-r-2xl',
                boardsTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  All Boards
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Boards overview + charts (GraphQL taskBoardsOverview; not from paginated master list) -->
        <div class="mb-6">
          <div v-if="isBoardsOverviewLoading" class="space-y-6">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div v-for="i in 3" :key="'boards-overview-skel-' + i"
                class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 animate-pulse">
                <div class="h-3 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div class="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div v-for="j in 3" :key="'boards-chart-skel-' + j"
                class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 animate-pulse">
                <div class="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div class="h-64 bg-gray-100 rounded"></div>
              </div>
            </div>
          </div>
          <div v-else-if="boardsOverviewPayloadError"
            class="bg-white border border-red-200 rounded-2xl shadow-sm p-5 text-sm text-red-700">
            {{ boardsOverviewErrorMessage }}
          </div>
          <template v-else>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Boards</div>
                <div class="mt-2 flex items-end justify-between gap-3">
                  <div class="text-3xl font-bold text-slate-900 tabular-nums">{{
                    boardsOverviewForTiles.totalMasterBoards }}</div>
                  <div class="text-xs text-slate-500">Master boards</div>
                </div>
              </div>
              <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Sub Boards</div>
                <div class="mt-2 flex items-end justify-between gap-3">
                  <div class="text-3xl font-bold text-slate-900 tabular-nums">{{ boardsOverviewForTiles.totalSubBoards
                  }}</div>
                  <div class="text-xs text-slate-500">Across all boards</div>
                </div>
              </div>
              <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Top Sub-board (Most Tasks)
                </div>
                <div class="mt-2">
                  <div v-if="boardsOverviewForTiles.topSubBoardName" class="min-w-0">
                    <div class="text-base font-semibold text-slate-900 truncate">
                      {{ boardsOverviewForTiles.topSubBoardName }}
                    </div>
                    <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                      <span class="font-medium text-slate-700 tabular-nums">{{
                        boardsOverviewForTiles.topSubBoardTaskCount }}</span>
                      <span>{{ boardsOverviewForTiles.topSubBoardTaskCount === 1 ? 'task' : 'tasks' }}</span>
                      <template v-if="boardsOverviewForTiles.topSubBoardMasterName">
                        <span class="text-slate-300" aria-hidden="true">·</span>
                        <span class="truncate">in {{ boardsOverviewForTiles.topSubBoardMasterName }}</span>
                      </template>
                    </div>
                  </div>
                  <div v-else class="text-sm text-slate-500">No sub-boards in scope.</div>
                </div>
              </div>
            </div>
            <div v-if="boardsOverviewChartsVisible" class="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div v-if="masterSubBoardsChartCategories.length > 0"
                class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <h3 class="text-base font-semibold text-slate-900">Sub-boards per master board</h3>
                <p class="mt-1 text-xs text-slate-500">
                  Top 5 master groups by sub-board count
                  <template v-if="masterSubBoardsChartOtherBarIndex >= 0"> plus Other (click Other to see every
                    remaining group).</template>
                </p>
                <div class="mt-4">
                  <apexchart type="bar" height="300" :options="boardsSubBoardsPerMasterChartOptions"
                    :series="boardsSubBoardsPerMasterChartSeries"
                    @dataPointSelection="onMasterSubBoardsPerMasterChartPointSelect" />
                </div>
              </div>
              <div v-if="masterTasksChartCategories.length > 0"
                class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <h3 class="text-base font-semibold text-slate-900">Total tasks per master board</h3>
                <p class="mt-1 text-xs text-slate-500">
                  Top 5 master groups by total tasks
                  <template v-if="masterTasksChartOtherBarIndex >= 0"> plus Other (click Other to see every remaining
                    group).</template>
                </p>
                <div class="mt-4">
                  <apexchart type="bar" height="300" :options="boardsTasksPerMasterChartOptions"
                    :series="boardsTasksPerMasterChartSeries"
                    @dataPointSelection="onMasterTasksPerMasterChartPointSelect" />
                </div>
              </div>
              <div v-if="subBoardTaskChartCategories.length > 0"
                class="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <h3 class="text-base font-semibold text-slate-900">Tasks per sub-board</h3>
                <p class="mt-1 text-xs text-slate-500">
                  Top 5 sub-boards by task count
                  <template v-if="subBoardTaskChartOtherBarIndex >= 0"> plus Other (click Other to see every remaining
                    sub-board).</template>
                </p>
                <div class="mt-4">
                  <apexchart type="bar" height="300" :options="boardsSubBoardTasksChartOptions"
                    :series="boardsSubBoardTasksChartSeries" @dataPointSelection="onSubBoardTasksChartPointSelect" />
                </div>
              </div>
            </div>
            <div v-else class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-slate-500">
              No chart data for this scope.
            </div>
          </template>
        </div>

        <!-- Boards Loading State (initial fetch only; load-more keeps the list visible) -->
        <div v-if="showBoardsInitialLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="i in 3" :key="i" class="bg-white rounded-xl shadow-md p-6 animate-pulse">
            <div class="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>

        <!-- Master Boards with nested Sub-Boards -->
        <div v-else-if="masterBoards && masterBoards.length > 0" class="space-y-10">
          <div v-for="(masterBoard, masterIndex) in masterBoards" :key="masterBoard.id"
            class="boards-master-shell overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
            <!-- Master header: section index = solid disc (distinct from sub-board # list) -->
            <div
              class="flex flex-col gap-4 border-b border-slate-100 bg-gradient-to-br from-white via-white to-slate-50/90 px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:px-6"
              :class="parseInt(masterBoard.id) !== 0 ? 'cursor-pointer sm:hover:bg-slate-50/90' : ''"
              @click="parseInt(masterBoard.id) !== 0 && openMasterBoard(masterBoard.id)">
              <div class="flex min-w-0 flex-1 gap-4">
                <div
                  class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-base font-bold tabular-nums text-white shadow-md shadow-slate-900/15 ring-4 ring-slate-900/[0.07]"
                  aria-hidden="true">
                  {{ masterIndex + 1 }}
                </div>
                <div class="min-w-0 flex-1 border-l-[3px] pl-4"
                  :style="{ borderColor: masterBoard.color || '#499DD4' }">
                  <h2 class="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">{{ masterBoard.name }}</h2>
                  <p v-if="masterBoard.description" class="mt-1 text-sm leading-relaxed text-slate-500 line-clamp-2">
                    {{ masterBoard.description }}
                  </p>
                  <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
                    <span v-if="masterBoard.created_by && masterBoard.created_by.name">
                      <span class="text-slate-400">Created by</span>
                      <span class="font-medium text-slate-700">{{ masterBoard.created_by.name }}</span>
                    </span>
                    <span class="hidden text-slate-300 sm:inline" aria-hidden="true">·</span>
                    <span role="button" tabindex="0" @click.stop="openBoardMembersModal(masterBoard, 'master')"
                      @keydown.enter.stop.prevent="openBoardMembersModal(masterBoard, 'master')"
                      class="inline-flex cursor-pointer rounded-full bg-slate-100/80 px-2.5 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1">
                      {{ getMembersCount(masterBoard) }}
                      {{ getMembersCount(masterBoard) === 1 ? 'member' : 'members' }}
                    </span>
                    <span class="hidden text-slate-300 sm:inline" aria-hidden="true">·</span>
                    <span class="font-medium text-slate-600">{{ getSubBoards(masterBoard).length }}
                      {{ getSubBoards(masterBoard).length === 1 ? 'sub-board' : 'sub-boards' }}</span>
                    <template v-if="masterBoard.attachments && masterBoard.attachments.length > 0">
                      <span class="hidden text-slate-300 sm:inline" aria-hidden="true">·</span>
                      <span class="font-medium text-slate-600">
                        {{ masterBoard.attachments.length }}
                        {{ masterBoard.attachments.length === 1 ? 'attachment' : 'attachments' }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="flex shrink-0 flex-wrap items-center gap-2 sm:pt-0.5">
                <button v-if="parseInt(masterBoard.id) !== 0" @click.stop="openCreateBoardForMasterBoard(masterBoard)"
                  class="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-white px-3 py-1.5 text-sm font-medium text-primary shadow-sm transition-colors hover:bg-primary hover:text-white"
                  title="New sub-board">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  New sub-board
                </button>
                <button v-else @click.stop="openCreateBoardForMasterBoard(null)"
                  class="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-white px-3 py-1.5 text-sm font-medium text-primary shadow-sm transition-colors hover:bg-primary hover:text-white"
                  title="New sub-board">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  New sub-board
                </button>
                <button v-if="canEditMasterBoard(masterBoard)" @click.stop="openEditMasterBoard(masterBoard)"
                  class="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  title="Edit master board">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button v-if="parseInt(masterBoard.id) !== 0" @click.stop="openMasterBoard(masterBoard.id)"
                  class="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  title="View master board details">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button v-if="canDeleteBoards && parseInt(masterBoard.id) !== 0"
                  @click.stop="confirmDeleteMasterBoard(masterBoard)"
                  class="rounded-lg border border-slate-200 bg-white p-2 text-red-600 transition-colors hover:border-red-200 hover:bg-red-50"
                  title="Delete master board">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0H7m2 0V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="bg-slate-50/50 px-4 py-6 sm:px-6">
              <div v-if="getSubBoards(masterBoard).length > 0"
                class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div v-for="(board, subIndex) in getSubBoards(masterBoard)" :key="board.id" @click="openBoard(board.id)"
                  class="boards-sub-card group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md">
                  <div class="h-1 w-full shrink-0" :style="{ backgroundColor: board.color || '#94a3b8' }"></div>
                  <div class="flex flex-1 flex-col p-4 pt-3">
                    <div class="mb-3 flex items-start justify-between gap-3">
                      <!-- Sub-board index: monospace # + two digits (not a badge — reads as row / list index) -->
                      <div class="flex min-w-0 flex-1 gap-3">
                        <div
                          class="flex w-9 shrink-0 flex-col items-end justify-center border-r border-slate-200/90 pr-2 text-right"
                          aria-hidden="true">
                          <span class="font-mono text-sm font-semibold tabular-nums leading-tight text-slate-500">#{{
                            formatBoardOrdinal(subIndex + 1) }}</span>
                        </div>
                        <div class="min-w-0 flex-1">
                          <h3
                            class="truncate text-base font-semibold leading-snug text-slate-900 group-hover:text-primary">
                            {{ board.name }}
                          </h3>
                        </div>
                      </div>
                      <div class="flex shrink-0 items-center gap-1">
                        <button v-if="canEditBoard(board)" @click.stop="openEditBoard(board)"
                          class="rounded-lg border border-transparent p-1.5 text-slate-500 transition-colors hover:border-slate-200 hover:bg-slate-50 hover:text-slate-800"
                          title="Edit board">
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button v-if="canDeleteBoards" @click.stop="confirmDeleteBoard(board)"
                          class="rounded-lg border border-transparent p-1.5 text-red-500 transition-colors hover:border-red-100 hover:bg-red-50"
                          title="Delete board">
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0H7m2 0V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p v-if="board.created_by && board.created_by.name" class="mb-2 text-xs text-slate-500">
                      <span class="text-slate-400">Created by</span>
                      <span class="font-medium text-slate-700">{{ board.created_by.name }}</span>
                    </p>
                    <p v-if="board.description" class="mb-3 line-clamp-2 text-sm leading-relaxed text-slate-600">
                      {{ board.description }}
                    </p>
                    <div class="mt-auto flex flex-col border-t border-slate-100 pt-3">
                      <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-slate-500">
                        <span class="font-medium text-slate-600">Tasks · {{ getTaskStats(board).total }} total</span>
                        <span role="button" tabindex="0" @click.stop="openBoardMembersModal(board, 'sub')"
                          @keydown.enter.stop.prevent="openBoardMembersModal(board, 'sub')"
                          class="cursor-pointer rounded-full bg-slate-100/90 px-2 py-0.5 font-medium text-slate-700 transition-colors hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1">
                          {{ getMembersCount(board) }}
                          {{ getMembersCount(board) === 1 ? 'member' : 'members' }}
                        </span>
                      </div>
                      <div v-if="board.attachments && board.attachments.length > 0" class="mt-1 text-xs text-slate-500">
                        {{ board.attachments.length }}
                        {{ board.attachments.length === 1 ? 'attachment' : 'attachments' }}
                      </div>
                      <div v-if="getTaskStats(board).total > 0"
                        class="mt-3 space-y-1 rounded-lg border border-slate-100 bg-slate-50/80 p-2.5 text-xs text-slate-600">
                        <div v-if="getTaskStats(board).todo > 0" class="flex items-center justify-between gap-2">
                          <span class="font-medium text-slate-700">To Do</span>
                          <span class="tabular-nums">{{ getTaskStats(board).todo }}</span>
                        </div>
                        <div v-if="getTaskStats(board).in_progress > 0" class="flex items-center justify-between gap-2">
                          <span class="font-medium text-slate-700">In Progress</span>
                          <span class="tabular-nums">{{ getTaskStats(board).in_progress }}</span>
                        </div>
                        <div v-if="getTaskStats(board).paused > 0" class="flex items-center justify-between gap-2">
                          <span class="font-medium text-slate-700">Paused</span>
                          <span class="tabular-nums">{{ getTaskStats(board).paused }}</span>
                        </div>
                        <div v-if="getTaskStats(board).in_review > 0" class="flex items-center justify-between gap-2">
                          <span class="font-medium text-slate-700">In Review</span>
                          <span class="tabular-nums">{{ getTaskStats(board).in_review }}</span>
                        </div>
                        <div v-if="getTaskStats(board).done > 0" class="flex items-center justify-between gap-2">
                          <span class="font-medium text-slate-700">Done</span>
                          <span class="tabular-nums">{{ getTaskStats(board).done }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-8 text-gray-500">
                <p class="text-sm">No sub-boards yet.</p>
                <div class="mt-3 flex flex-wrap justify-center gap-2">
                  <button v-if="parseInt(masterBoard.id) !== 0" @click="openCreateBoardForMasterBoard(masterBoard)"
                    class="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white">
                    New sub-board
                  </button>
                  <button v-else @click="openCreateBoardForMasterBoard(null)"
                    class="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white">
                    New sub-board
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Infinite scroll (same scroll target as tasks: #dashboard-scroll-container) -->
          <div v-if="boardsPaginatorInfo" class="mt-6 flex flex-col items-center gap-2">
            <div v-if="boardsLoadingMore" class="text-center py-2 text-sm text-slate-500">Loading more…</div>
            <div v-else-if="!boardsPaginatorInfo.hasMorePages && masterBoards.length > 0"
              class="text-center py-2 text-xs text-slate-400">
              No more boards
            </div>
            <p class="text-sm text-slate-500 tabular-nums">
              Showing {{ masterBoards.length }} of {{ boardsPaginatorInfo.total }}
              {{ boardsPaginatorInfo.total === 1 ? 'board group' : 'board groups' }}
            </p>
          </div>
        </div>

        <!-- Boards Empty State -->
        <div v-else class="text-center py-16">
          <div class="max-w-md mx-auto">
            <svg class="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2">
              </path>
            </svg>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">No Boards Yet</h3>
            <p class="text-gray-600 mb-6">Create your first board to organize your task boards</p>
            <button @click="showCreateMasterBoardModal = true"
              class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 inline-flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>Create Your First Board</span>
            </button>
          </div>
        </div>
      </template>

      <!-- Create Master Board Modal -->
      <transition name="fade" mode="out-in">
        <base-full-modal v-if="showCreateMasterBoardModal" title="Create Board"
          @close="showCreateMasterBoardModal = false">
          <create-master-board-modal class="text-left px-6 py-6" @close="showCreateMasterBoardModal = false"
            @created="handleMasterBoardCreated"></create-master-board-modal>
        </base-full-modal>
      </transition>

      <!-- Create Board Modal (Sub-Board) -->
      <transition name="fade" mode="out-in">
        <base-full-modal v-if="showCreateBoardModal" title="Create New Sub-Board" @close="closeCreateBoardModal">
          <create-board-modal class="text-left px-6 py-6" :can-view-all-boards="canViewAllBoards"
            :preselected-master-board-id="preselectedMasterBoardId"
            :preselected-master-board-name="preselectedMasterBoardName"
            :preselected-master-board-members="preselectedMasterBoardMembers" @close="closeCreateBoardModal"
            @created="handleBoardCreated"></create-board-modal>
        </base-full-modal>
      </transition>

      <!-- Step 1: overflow visible (board multiselect). Step 2: overflow auto so the task form scrolls. -->
      <base-modal v-if="showCreateTaskModal" title="Create New Task"
        :content-overflow="createTaskModalLayout === 1 ? 'visible' : 'auto'" @close="closeCreateTaskModal">
        <create-task-modal-standalone @close="closeCreateTaskModal" @created="handleTaskCreated"
          @layout-step="createTaskModalLayout = $event" />
      </base-modal>

      <!-- Task Detail Modal -->
      <base-modal v-if="selectedTaskId" title="Task Details" @close="selectedTaskId = null">
        <task-detail-modal :task-id="selectedTaskId" :board-members="[]" @close="selectedTaskId = null"
          @updated="handleTaskUpdated" @deleted="handleTaskDeleted" />
      </base-modal>

      <!-- Edit Board Modal (Sub-Board) -->
      <base-full-modal v-if="showEditBoardModal && selectedBoard" title="Edit Sub-Board" @close="closeEditBoard">
        <edit-board-modal class="text-left px-6 py-6" :board="selectedBoard" :can-view-all-boards="canViewAllBoards"
          @close="closeEditBoard" @saved="handleBoardUpdated"></edit-board-modal>
      </base-full-modal>

      <!-- Edit Master Board Modal -->
      <transition name="fade" mode="out-in">
        <base-full-modal v-if="showEditMasterBoardModal && selectedMasterBoard" title="Edit Master Board"
          @close="closeEditMasterBoard">
          <edit-master-board-modal class="text-left px-6 py-6" :master-board="selectedMasterBoard"
            @close="closeEditMasterBoard" @saved="handleMasterBoardUpdated"></edit-master-board-modal>
        </base-full-modal>
      </transition>

      <!-- Other master boards: breakdown (from taskBoardsMasterBoardBreakdownAfterTop) -->
      <base-modal v-if="showMasterOtherModal" :title="masterOtherModalTitle" @close="closeMasterOtherModal">
        <div class="px-8 py-2">
          <div v-if="masterOtherModalLoading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          </div>
          <div v-else-if="masterOtherModalError" class="text-sm text-red-600 py-4">{{ masterOtherModalError }}</div>
          <div v-else-if="masterOtherModalRows.length === 0" class="text-sm text-slate-500 py-4 text-center">
            No additional master groups.
          </div>
          <div v-else class="max-h-96 overflow-y-auto divide-y divide-gray-100">
            <div v-for="(row, idx) in masterOtherModalRows" :key="row.master_board_id"
              class="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
              <div class="flex min-w-0 flex-1 items-start gap-3">
                <div
                  class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-700">
                  {{ idx + 1 }}
                </div>
                <div class="min-w-0 flex-1">
                  <p class="font-medium text-slate-900 truncate">{{ row.master_board_name }}</p>
                  <p class="text-xs text-slate-500 tabular-nums">
                    {{ row.sub_board_count }} {{ row.sub_board_count === 1 ? 'sub-board' : 'sub-boards' }}
                    <span class="text-slate-300" aria-hidden="true"> · </span>
                    {{ row.total_tasks }} {{ row.total_tasks === 1 ? 'task' : 'tasks' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </base-modal>

      <!-- Other sub-boards: task breakdown (from taskBoardsSubBoardTasksAfterTop) -->
      <base-modal v-if="showSubBoardOtherModal" title="Other sub-boards — tasks" @close="closeSubBoardOtherModal">
        <div class="px-8 py-2">
          <div v-if="subBoardOtherModalLoading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          </div>
          <div v-else-if="subBoardOtherModalError" class="text-sm text-red-600 py-4">{{ subBoardOtherModalError }}</div>
          <div v-else-if="subBoardOtherModalRows.length === 0" class="text-sm text-slate-500 py-4 text-center">
            No additional sub-boards in this group.
          </div>
          <div v-else class="max-h-96 overflow-y-auto divide-y divide-gray-100">
            <div class="sticky top-0 z-10 grid grid-cols-12 gap-3 bg-white py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <div class="col-span-1">#</div>
              <div class="col-span-8">Sub-Board</div>
              <div class="col-span-3 text-right">Tasks Count</div>
            </div>
            <div
              v-for="(row, idx) in subBoardOtherModalRows"
              :key="row.board_id"
              class="grid grid-cols-12 items-start gap-3 py-3 text-sm"
            >
              <div class="col-span-1 font-semibold tabular-nums text-slate-700">{{ idx + 1 }}</div>
              <div class="col-span-8 min-w-0">
                <p class="font-medium text-slate-900 truncate">{{ row.board_name }}</p>
                <p v-if="row.master_board_name" class="text-xs text-slate-500 truncate">{{ row.master_board_name }}</p>
              </div>
              <div class="col-span-3 text-right font-semibold tabular-nums text-slate-700">{{ row.task_count }}</div>
            </div>
          </div>
        </div>
      </base-modal>

      <!-- Board Members Modal (master board or sub-board) -->
      <base-modal v-if="selectedBoardForMembers" :title="boardMembersModalTitle" @close="closeBoardMembersModal">
        <div class="space-y-4 max-h-96 overflow-y-auto">
          <div v-for="member in (selectedBoardForMembers.members || [])" :key="member.id"
            class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            title="Open profile in new tab" @click="goToProfile(member)">
            <div
              class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden ring-2 ring-white shadow-md cursor-pointer hover:ring-blue-200 transition-all flex-shrink-0">
              <img :src="getProfilePicture(member.profile_picture_url)" :alt="member.name"
                class="w-full h-full object-cover cursor-pointer" @error="handleImageError" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline truncate">
                {{ member.name }}
              </p>
              <p class="text-xs text-gray-500 truncate">{{ member.email }}</p>
              <span v-if="isCreator(member, selectedBoardForMembers)"
                class="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md">
                Creator
              </span>
            </div>
          </div>
        </div>
      </base-modal>
    </div>
  </section>
</template>

<script>
import gql from 'graphql-tag';

const TASK_BOARDS_SUB_BOARD_TASKS_AFTER_TOP = gql`
  query TaskBoardsSubBoardTasksAfterTop($scope: TaskBoardsOverviewScope!, $top_n: Int!) {
    taskBoardsSubBoardTasksAfterTop(scope: $scope, top_n: $top_n) {
      error
      error_code
      message
      rows {
        board_id
        board_name
        master_board_name
        task_count
      }
    }
  }
`;

const TASK_BOARDS_MASTER_BREAKDOWN_AFTER_TOP = gql`
  query TaskBoardsMasterBreakdownAfterTop(
    $scope: TaskBoardsOverviewScope!
    $metric: TaskBoardsMasterChartMetric!
    $top_n: Int!
  ) {
    taskBoardsMasterBoardBreakdownAfterTop(scope: $scope, metric: $metric, top_n: $top_n) {
      error
      error_code
      message
      rows {
        master_board_id
        master_board_name
        sub_board_count
        total_tasks
      }
    }
  }
`;

import CreateBoardModal from './components/CreateBoardModal.vue';
import EditBoardModal from './components/EditBoardModal.vue';
import CreateMasterBoardModal from './components/CreateMasterBoardModal.vue';
import EditMasterBoardModal from './components/EditMasterBoardModal.vue';
import CreateTaskModalStandalone from './components/CreateTaskModalStandalone.vue';
import TaskDetailModal from './components/TaskDetailModal.vue';
import TasksCharts from './components/TasksCharts.vue';

export default {
  name: 'tasks-index',
  components: {
    CreateBoardModal,
    EditBoardModal,
    CreateMasterBoardModal,
    EditMasterBoardModal,
    CreateTaskModalStandalone,
    TaskDetailModal,
    TasksCharts,
  },
  data() {
    return {
      showCreateBoardModal: false,
      showCreateMasterBoardModal: false,
      showCreateTaskModal: false,
      selectedTaskId: null,
      showEditBoardModal: false,
      showEditMasterBoardModal: false,
      selectedBoard: null,
      selectedMasterBoard: null,
      selectedBoardForMembers: null,
      preselectedMasterBoardId: null,
      preselectedMasterBoardName: null,
      preselectedMasterBoardMembers: [],
      boardsFirst: 5,
      boardsPage: 1,
      boardsLoadingMore: false,
      viewMode: 'tasks',
      activeTab: 'my',
      boardsTab: 'my',
      tasksFirst: 10,
      tasksPage: 1,
      taskStatusFilter: 'all',
      tasksLoadingMore: false,
      createTaskModalLayout: 1,
      membersModalKind: 'sub',
      tasksListSearch: '',
      tasksListSearchDebounced: '',
      tasksListSearchDebounceTimer: null,
      showSubBoardOtherModal: false,
      subBoardOtherModalRows: [],
      subBoardOtherModalLoading: false,
      subBoardOtherModalError: '',
      showMasterOtherModal: false,
      masterOtherModalRows: [],
      masterOtherModalLoading: false,
      masterOtherModalError: '',
      masterOtherModalMetric: 'SUB_BOARDS_PER_MASTER',
    };
  },
  computed: {
    masterOtherModalTitle() {
      if (this.masterOtherModalMetric === 'TASKS_PER_MASTER') {
        return 'Other master boards — tasks';
      }
      return 'Other master boards — sub-boards';
    },
    boardMembersModalTitle() {
      if (!this.selectedBoardForMembers) {
        return '';
      }
      var name = this.selectedBoardForMembers.name ? this.selectedBoardForMembers.name : 'Board';
      if (this.membersModalKind === 'master') {
        return 'Board members — ' + name;
      }
      return 'Sub-board members — ' + name;
    },
    taskStatusOptions() {
      return [
        { value: 'all', label: 'All' },
        { value: 'todo', label: 'To Do' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'paused', label: 'Paused' },
        { value: 'in_review', label: 'In Review' },
        { value: 'done', label: 'Done' },
      ];
    },
    tasksListSearchActiveHint() {
      return (this.tasksListSearch || '').trim().length > 0;
    },
    hasTasksListSearchFilter() {
      return (this.tasksListSearchDebounced || '').trim().length > 0;
    },
    emptyTasksListTitle() {
      if (this.hasTasksListSearchFilter) {
        return 'No matching tasks';
      }
      return 'No Tasks Yet';
    },
    emptyTasksListDescription() {
      if (this.hasTasksListSearchFilter) {
        return 'No tasks match your search. Try different keywords or clear the search field.';
      }
      if (this.taskStatusFilter !== 'all') {
        return 'No tasks match this status.';
      }
      if (this.activeTab === 'my') {
        return 'You have no tasks assigned to you.';
      }
      if (this.activeTab === 'viewer') {
        return 'You are not a viewer on any tasks.';
      }
      return 'No tasks in your boards yet.';
    },
    taskListStatusCounts() {
      let raw;
      if (this.activeTab === 'my') {
        raw = this.myTasksAcrossBoardsStatusCounts;
      } else if (this.activeTab === 'viewer') {
        raw = this.viewerTasksAcrossBoardsStatusCounts;
      } else {
        raw = this.allTasksAcrossBoardsStatusCounts;
      }
      if (!raw || typeof raw !== 'object') {
        return {
          all: 0,
          todo: 0,
          in_progress: 0,
          paused: 0,
          in_review: 0,
          done: 0,
        };
      }
      return raw;
    },
    isTaskStatusCountsLoading() {
      if (this.viewMode !== 'tasks') {
        return false;
      }
      let q;
      if (this.activeTab === 'my') {
        q = this.$apollo.queries.myTasksAcrossBoardsStatusCounts;
      } else if (this.activeTab === 'viewer') {
        q = this.$apollo.queries.viewerTasksAcrossBoardsStatusCounts;
      } else {
        q = this.$apollo.queries.allTasksAcrossBoardsStatusCounts;
      }
      return q && q.loading;
    },
    canViewAllBoards() {
      const permissions = this.$store.getters.permissions;
      const permissionsArray = permissions.split(",");
      if (permissionsArray.includes('view-all-task-management-board')) {
        return true;
      } else {
        return false;
      }
    },
    isTasksLoading() {
      if (this.activeTab === 'my') {
        return this.$apollo.queries.myTasksAcrossBoards && this.$apollo.queries.myTasksAcrossBoards.loading;
      }
      if (this.activeTab === 'viewer') {
        return this.$apollo.queries.viewerTasksAcrossBoards && this.$apollo.queries.viewerTasksAcrossBoards.loading;
      }
      return this.$apollo.queries.allTasksAcrossBoards && this.$apollo.queries.allTasksAcrossBoards.loading;
    },
    isBoardsLoading() {
      if (this.canViewAllBoards && this.boardsTab === 'all') {
        return this.$apollo.queries.allTaskMasterBoards && this.$apollo.queries.allTaskMasterBoards.loading;
      }
      return this.$apollo.queries.taskMasterBoards && this.$apollo.queries.taskMasterBoards.loading;
    },
    boardsPaginatorInfo() {
      const payload =
        this.canViewAllBoards && this.boardsTab === 'all' ? this.allTaskMasterBoards : this.taskMasterBoards;
      if (!payload || payload.error) {
        return null;
      }
      return payload.paginatorInfo || null;
    },
    showBoardsInitialLoading() {
      if (this.viewMode !== 'boards') {
        return false;
      }
      let q;
      if (this.canViewAllBoards && this.boardsTab === 'all') {
        q = this.$apollo.queries.allTaskMasterBoards;
      } else {
        q = this.$apollo.queries.taskMasterBoards;
      }
      if (!q || !q.loading) {
        return false;
      }
      const boards = this.masterBoards;
      return !boards || boards.length === 0;
    },
    displayTasks() {
      let payload;
      if (this.activeTab === 'my') payload = this.myTasksAcrossBoards;
      else if (this.activeTab === 'viewer') payload = this.viewerTasksAcrossBoards;
      else payload = this.allTasksAcrossBoards;
      if (!payload || payload.error) return [];
      return payload.data || [];
    },
    tasksPaginatorInfo() {
      let payload;
      if (this.activeTab === 'my') payload = this.myTasksAcrossBoards;
      else if (this.activeTab === 'viewer') payload = this.viewerTasksAcrossBoards;
      else payload = this.allTasksAcrossBoards;
      if (!payload || payload.error) return null;
      return payload.paginatorInfo || null;
    },
    masterBoards() {
      const payload = (this.canViewAllBoards && this.boardsTab === 'all') ? this.allTaskMasterBoards : this.taskMasterBoards;
      if (!payload || payload.error) return [];
      return payload.data || [];
    },
    isBoardsOverviewLoading() {
      if (this.viewMode !== 'boards') {
        return false;
      }
      const q = this.$apollo.queries.taskBoardsOverview;
      return q && q.loading;
    },
    boardsOverviewPayloadError() {
      if (this.viewMode !== 'boards') {
        return false;
      }
      const p = this.taskBoardsOverview;
      return p && p.error;
    },
    boardsOverviewErrorMessage() {
      const p = this.taskBoardsOverview;
      if (p && p.message) {
        return p.message;
      }
      return 'Could not load boards overview.';
    },
    /** Summary tiles from GraphQL taskBoardsOverview (full scope, not paginated list). */
    boardsOverviewForTiles() {
      const p = this.taskBoardsOverview;
      if (!p || p.error || !p.summary) {
        return {
          totalMasterBoards: 0,
          totalSubBoards: 0,
          topSubBoardName: '',
          topSubBoardTaskCount: 0,
          topSubBoardMasterName: '',
        };
      }
      const s = p.summary;
      const topCount = s.top_sub_board_task_count;
      const topCountNum = typeof topCount === 'number' ? topCount : parseInt(topCount, 10);
      const tm = s.total_master_boards;
      const tsb = s.total_sub_boards;
      const tmNum = typeof tm === 'number' ? tm : parseInt(tm, 10);
      const tsbNum = typeof tsb === 'number' ? tsb : parseInt(tsb, 10);
      return {
        totalMasterBoards: !isNaN(tmNum) ? tmNum : 0,
        totalSubBoards: !isNaN(tsbNum) ? tsbNum : 0,
        topSubBoardName: s.top_sub_board_name ? String(s.top_sub_board_name) : '',
        topSubBoardTaskCount: !isNaN(topCountNum) ? topCountNum : 0,
        topSubBoardMasterName: s.top_sub_board_master_name ? String(s.top_sub_board_master_name) : '',
      };
    },
    boardsOverviewChartsVisible() {
      return (
        this.masterSubBoardsChartCategories.length > 0 ||
        this.masterTasksChartCategories.length > 0 ||
        this.subBoardTaskChartCategories.length > 0
      );
    },
    masterSubBoardsChartRowsFromOverview() {
      const p = this.taskBoardsOverview;
      if (!p || p.error || !p.sub_boards_per_master_chart || !Array.isArray(p.sub_boards_per_master_chart)) {
        return [];
      }
      return p.sub_boards_per_master_chart;
    },
    masterSubBoardsChartOtherBarIndex() {
      const rows = this.masterSubBoardsChartRowsFromOverview;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          return i;
        }
      }
      return -1;
    },
    masterSubBoardsChartCategories() {
      const rows = this.masterSubBoardsChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        let name = r && r.master_board_name ? String(r.master_board_name) : '';
        if (name.length > 22) {
          name = name.substring(0, 19) + '...';
        }
        out.push(name);
      }
      return out;
    },
    masterSubBoardsChartSeriesValues() {
      const rows = this.masterSubBoardsChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const mv = r && r.metric_value;
        const n = typeof mv === 'number' ? mv : parseInt(mv, 10);
        out.push(!isNaN(n) ? n : 0);
      }
      return out;
    },
    masterSubBoardsChartBarColors() {
      const rows = this.masterSubBoardsChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          out.push('#94A3B8');
        } else {
          out.push('#2563EB');
        }
      }
      return out;
    },
    boardsSubBoardsPerMasterChartSeries() {
      return [{ name: 'Sub-boards', data: this.masterSubBoardsChartSeriesValues }];
    },
    boardsSubBoardsPerMasterChartOptions() {
      const cat = this.masterSubBoardsChartCategories;
      const colors = this.masterSubBoardsChartBarColors;
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '58%',
            distributed: true,
          },
        },
        colors: colors,
        legend: { show: false },
        dataLabels: {
          enabled: true,
          offsetY: -4,
          style: { fontSize: '11px', colors: ['#334155'] },
        },
        xaxis: {
          categories: cat,
          labels: {
            style: { fontSize: '11px' },
            rotate: -35,
            rotateAlways: true,
          },
        },
        yaxis: {
          title: {
            text: 'Sub-boards',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
          labels: { style: { fontSize: '11px' } },
        },
        grid: {
          borderColor: '#e2e8f0',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const v = typeof val === 'number' ? val : 0;
              return String(v) + (v === 1 ? ' sub-board' : ' sub-boards');
            },
          },
        },
      };
    },
    masterTasksChartRowsFromOverview() {
      const p = this.taskBoardsOverview;
      if (!p || p.error || !p.tasks_per_master_chart || !Array.isArray(p.tasks_per_master_chart)) {
        return [];
      }
      return p.tasks_per_master_chart;
    },
    masterTasksChartOtherBarIndex() {
      const rows = this.masterTasksChartRowsFromOverview;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          return i;
        }
      }
      return -1;
    },
    masterTasksChartCategories() {
      const rows = this.masterTasksChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        let name = r && r.master_board_name ? String(r.master_board_name) : '';
        if (name.length > 22) {
          name = name.substring(0, 19) + '...';
        }
        out.push(name);
      }
      return out;
    },
    masterTasksChartSeriesValues() {
      const rows = this.masterTasksChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const mv = r && r.metric_value;
        const n = typeof mv === 'number' ? mv : parseInt(mv, 10);
        out.push(!isNaN(n) ? n : 0);
      }
      return out;
    },
    masterTasksChartBarColors() {
      const rows = this.masterTasksChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          out.push('#94A3B8');
        } else {
          out.push('#0D9488');
        }
      }
      return out;
    },
    boardsTasksPerMasterChartSeries() {
      return [{ name: 'Tasks', data: this.masterTasksChartSeriesValues }];
    },
    boardsTasksPerMasterChartOptions() {
      const cat = this.masterTasksChartCategories;
      const colors = this.masterTasksChartBarColors;
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '58%',
            distributed: true,
          },
        },
        colors: colors,
        legend: { show: false },
        dataLabels: {
          enabled: true,
          offsetY: -4,
          style: { fontSize: '11px', colors: ['#334155'] },
        },
        xaxis: {
          categories: cat,
          labels: {
            style: { fontSize: '11px' },
            rotate: -35,
            rotateAlways: true,
          },
        },
        yaxis: {
          title: {
            text: 'Tasks',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
          labels: { style: { fontSize: '11px' } },
        },
        grid: {
          borderColor: '#e2e8f0',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const v = typeof val === 'number' ? val : 0;
              return String(v) + (v === 1 ? ' task' : ' tasks');
            },
          },
        },
      };
    },
    subBoardTaskChartRowsFromOverview() {
      const p = this.taskBoardsOverview;
      if (!p || p.error || !p.sub_board_task_chart || !Array.isArray(p.sub_board_task_chart)) {
        return [];
      }
      return p.sub_board_task_chart;
    },
    subBoardTaskChartOtherBarIndex() {
      const rows = this.subBoardTaskChartRowsFromOverview;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          return i;
        }
      }
      return -1;
    },
    subBoardTaskChartCategories() {
      const rows = this.subBoardTaskChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        let name = r && r.board_name ? String(r.board_name) : '';
        if (name.length > 22) {
          name = name.substring(0, 19) + '...';
        }
        out.push(name);
      }
      return out;
    },
    subBoardTaskChartSeriesValues() {
      const rows = this.subBoardTaskChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const tc = r && r.task_count;
        const n = typeof tc === 'number' ? tc : parseInt(tc, 10);
        out.push(!isNaN(n) ? n : 0);
      }
      return out;
    },
    subBoardTaskChartBarColors() {
      const rows = this.subBoardTaskChartRowsFromOverview;
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].is_other_aggregate) {
          out.push('#94A3B8');
        } else {
          out.push('#4F46E5');
        }
      }
      return out;
    },
    boardsSubBoardTasksChartSeries() {
      return [{ name: 'Tasks', data: this.subBoardTaskChartSeriesValues }];
    },
    boardsSubBoardTasksChartOptions() {
      const cat = this.subBoardTaskChartCategories;
      const colors = this.subBoardTaskChartBarColors;
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '58%',
            distributed: true,
          },
        },
        colors: colors,
        legend: { show: false },
        dataLabels: {
          enabled: true,
          offsetY: -4,
          style: { fontSize: '11px', colors: ['#334155'] },
        },
        xaxis: {
          categories: cat,
          labels: {
            style: { fontSize: '11px' },
            rotate: cat.length > 4 ? -35 : 0,
            rotateAlways: cat.length > 4,
          },
        },
        yaxis: {
          title: {
            text: 'Tasks',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
          labels: { style: { fontSize: '11px' } },
        },
        grid: {
          borderColor: '#e2e8f0',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const v = typeof val === 'number' ? val : 0;
              return String(v) + (v === 1 ? ' task' : ' tasks');
            },
          },
        },
      };
    },
    unassignedBoards() {
      const unassigned = this.masterBoards.find(function (mb) { return parseInt(mb.id) === 0; });
      return (unassigned && unassigned.sub_boards && Array.isArray(unassigned.sub_boards)) ? unassigned.sub_boards : [];
    },
    canEditAllBoards() {
      const permissions = this.$store.getters.permissions;
      const permissionsArray = permissions.split(",");
      if (permissionsArray.includes('view-edit-task-management-board')) {
        return true;
      } else {
        return false;
      }
    },
    canDeleteBoards() {
      const permissions = this.$store.getters.permissions;
      const permissionsArray = permissions.split(",");
      if (permissionsArray.includes('view-delete-task-management-board')) {
        return true;
      } else {
        return false;
      }
    },
    currentUserId() {
      const uid = this.$store.getters.userId;
      if (!uid) return null;
      return parseInt(uid);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.bindTasksScrollListener();
    });
  },
  watch: {
    tasksListSearch() {
      this.scheduleTasksListSearchDebounce();
    },
  },
  beforeUnmount() {
    if (this.tasksListSearchDebounceTimer) {
      clearTimeout(this.tasksListSearchDebounceTimer);
      this.tasksListSearchDebounceTimer = null;
    }
    this.unbindTasksScrollListener();
  },
  apollo: {
    taskMasterBoards: {
      query: gql`
        query GetTaskMasterBoards($first: Int!, $page: Int!) {
          taskMasterBoards(first: $first, page: $page) {
            error
            error_code
            message
            data {
              id
              token
              name
              description
              color
              is_active
              created_at
              created_by { id name }
              members { id token name email profile_picture_url }
              attachments {
                id
                file_name
                original_filename
                display_name
                file_path
                file_type
                file_size
              }
              sub_boards {
                id
                token
                name
                description
                color
                is_active
                master_board_id
                created_at
                created_by { id name }
                members { id token name email profile_picture_url }
                attachments {
                  id
                  file_name
                  original_filename
                  display_name
                  file_path
                  file_type
                  file_size
                }
                task_stats {
                  total
                  todo
                  in_progress
                  paused
                  in_review
                  done
                }
              }
            }
            paginatorInfo {
              count
              currentPage
              firstItem
              hasMorePages
              lastItem
              lastPage
              perPage
              total
            }
          }
        }
      `,
      variables() {
        return {
          first: this.boardsFirst,
          page: this.boardsPage,
        };
      },
      skip() {
        return this.viewMode !== 'boards' || (this.canViewAllBoards && this.boardsTab === 'all');
      },
      fetchPolicy: 'network-only',
    },
    myTasksAcrossBoards: {
      query: gql`
        query GetMyTasksAcrossBoards($first: Int!, $page: Int!, $status: String, $search: String) {
          myTasksAcrossBoards(first: $first, page: $page, status: $status, search: $search) {
            data {
              id token title description priority status due_date
              board { id name color master_board { id name } }
              column { id name }
              assignees { id name email }
              created_by { id name }
            }
            paginatorInfo {
              count currentPage firstItem hasMorePages lastItem lastPage perPage total
            }
          }
        }
      `,
      variables() {
        return {
          first: this.tasksFirst,
          page: 1,
          status: this.taskStatusFilter === 'all' ? null : this.taskStatusFilter,
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'my';
      },
      fetchPolicy: 'cache-and-network',
    },
    allTasksAcrossBoards: {
      query: gql`
        query GetAllTasksAcrossBoards($first: Int!, $page: Int!, $status: String, $search: String) {
          allTasksAcrossBoards(first: $first, page: $page, status: $status, search: $search) {
            data {
              id token title description priority status due_date
              board { id name color master_board { id name } }
              column { id name }
              assignees { id name email }
              created_by { id name }
            }
            paginatorInfo {
              count currentPage firstItem hasMorePages lastItem lastPage perPage total
            }
          }
        }
      `,
      variables() {
        return {
          first: this.tasksFirst,
          page: 1,
          status: this.taskStatusFilter === 'all' ? null : this.taskStatusFilter,
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'all';
      },
      fetchPolicy: 'cache-and-network',
    },
    viewerTasksAcrossBoards: {
      query: gql`
        query GetViewerTasksAcrossBoards($first: Int!, $page: Int!, $status: String, $search: String) {
          viewerTasksAcrossBoards(first: $first, page: $page, status: $status, search: $search) {
            data {
              id token title description priority status due_date
              board { id name color master_board { id name } }
              column { id name }
              assignees { id name email }
              created_by { id name }
            }
            paginatorInfo {
              count currentPage firstItem hasMorePages lastItem lastPage perPage total
            }
          }
        }
      `,
      variables() {
        return {
          first: this.tasksFirst,
          page: 1,
          status: this.taskStatusFilter === 'all' ? null : this.taskStatusFilter,
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'viewer';
      },
      fetchPolicy: 'cache-and-network',
    },
    myTasksAcrossBoardsStatusCounts: {
      query: gql`
        query GetMyTasksAcrossBoardsStatusCounts($search: String) {
          myTasksAcrossBoardsStatusCounts(search: $search) {
            all
            todo
            in_progress
            paused
            in_review
            done
          }
        }
      `,
      variables() {
        return {
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'my';
      },
      fetchPolicy: 'cache-and-network',
    },
    allTasksAcrossBoardsStatusCounts: {
      query: gql`
        query GetAllTasksAcrossBoardsStatusCounts($search: String) {
          allTasksAcrossBoardsStatusCounts(search: $search) {
            all
            todo
            in_progress
            paused
            in_review
            done
          }
        }
      `,
      variables() {
        return {
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'all';
      },
      fetchPolicy: 'cache-and-network',
    },
    viewerTasksAcrossBoardsStatusCounts: {
      query: gql`
        query GetViewerTasksAcrossBoardsStatusCounts($search: String) {
          viewerTasksAcrossBoardsStatusCounts(search: $search) {
            all
            todo
            in_progress
            paused
            in_review
            done
          }
        }
      `,
      variables() {
        return {
          search: this.getTasksListSearchGraphqlVar(),
        };
      },
      skip() {
        return this.viewMode !== 'tasks' || this.activeTab !== 'viewer';
      },
      fetchPolicy: 'cache-and-network',
    },
    allTaskMasterBoards: {
      query: gql`
        query GetAllTaskMasterBoards($first: Int!, $page: Int!) {
          allTaskMasterBoards(first: $first, page: $page) {
            error
            error_code
            message
            data {
              id
              token
              name
              description
              color
              is_active
              created_at
              created_by { id name }
              members { id token name email profile_picture_url }
              attachments {
                id
                file_name
                original_filename
                display_name
                file_path
                file_type
                file_size
              }
              sub_boards {
                id
                token
                name
                description
                color
                is_active
                master_board_id
                created_at
                created_by { id name }
                members { id token name email profile_picture_url }
                attachments {
                  id
                  file_name
                  original_filename
                  display_name
                  file_path
                  file_type
                  file_size
                }
                task_stats { total todo in_progress paused in_review done }
              }
            }
            paginatorInfo {
              count
              currentPage
              firstItem
              hasMorePages
              lastItem
              lastPage
              perPage
              total
            }
          }
        }
      `,
      variables() {
        return {
          first: this.boardsFirst,
          page: this.boardsPage,
        };
      },
      skip() {
        return this.viewMode !== 'boards' || !this.canViewAllBoards || this.boardsTab !== 'all';
      },
      fetchPolicy: 'network-only',
    },
    taskBoardsOverview: {
      query: gql`
        query TaskBoardsOverview($scope: TaskBoardsOverviewScope!) {
          taskBoardsOverview(scope: $scope) {
            error
            error_code
            message
            summary {
              total_master_boards
              total_sub_boards
              top_sub_board_id
              top_sub_board_name
              top_sub_board_task_count
              top_sub_board_master_name
            }
            by_master_board {
              master_board_id
              master_board_name
              sub_board_count
              total_tasks
            }
            sub_board_task_chart {
              board_id
              board_name
              master_board_name
              task_count
              is_other_aggregate
            }
            sub_boards_per_master_chart {
              master_board_id
              master_board_name
              metric_value
              is_other_aggregate
            }
            tasks_per_master_chart {
              master_board_id
              master_board_name
              metric_value
              is_other_aggregate
            }
          }
        }
      `,
      variables() {
        return {
          scope: this.getBoardsOverviewScope(),
        };
      },
      skip() {
        return this.viewMode !== 'boards';
      },
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    getTasksListSearchGraphqlVar() {
      const s = (this.tasksListSearchDebounced || '').trim();
      return s ? s : null;
    },
    getTasksListRefetchVariables() {
      const statusArg = this.taskStatusFilter === 'all' ? null : this.taskStatusFilter;
      return {
        first: this.tasksFirst,
        page: 1,
        status: statusArg,
        search: this.getTasksListSearchGraphqlVar(),
      };
    },
    /**
     * Use the ObservableQuery refetch directly. @vue/apollo-option's SmartQuery.refetch(vars)
     * sets options.variables to a plain object, which replaces the reactive variables() function
     * and stops status / search changes from re-running the list query.
     */
    observerRefetchSmartQuery(smartKey, variables) {
      const smart = this.$apollo.queries[smartKey];
      if (!smart || !smart.observer) {
        return Promise.resolve();
      }
      return smart.observer.refetch(variables);
    },
    getBoardsOverviewScope() {
      if (this.canViewAllBoards && this.boardsTab === 'all') {
        return 'ALL_BOARDS';
      }
      return 'MY_BOARDS';
    },
    refetchBoardsOverview() {
      var self = this;
      this.$nextTick(function () {
        self.observerRefetchSmartQuery('taskBoardsOverview', { scope: self.getBoardsOverviewScope() });
      });
    },
    onSubBoardTasksChartPointSelect(event, chartContext, config) {
      const idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      if (idx !== this.subBoardTaskChartOtherBarIndex) {
        return;
      }
      if (this.subBoardTaskChartOtherBarIndex < 0) {
        return;
      }
      this.loadSubBoardOtherBreakdown();
    },
    onMasterSubBoardsPerMasterChartPointSelect(event, chartContext, config) {
      const idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      if (idx !== this.masterSubBoardsChartOtherBarIndex) {
        return;
      }
      if (this.masterSubBoardsChartOtherBarIndex < 0) {
        return;
      }
      this.loadMasterOtherBreakdown('SUB_BOARDS_PER_MASTER');
    },
    onMasterTasksPerMasterChartPointSelect(event, chartContext, config) {
      const idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      if (idx !== this.masterTasksChartOtherBarIndex) {
        return;
      }
      if (this.masterTasksChartOtherBarIndex < 0) {
        return;
      }
      this.loadMasterOtherBreakdown('TASKS_PER_MASTER');
    },
    async loadMasterOtherBreakdown(metric) {
      this.masterOtherModalMetric = metric === 'TASKS_PER_MASTER' ? 'TASKS_PER_MASTER' : 'SUB_BOARDS_PER_MASTER';
      this.showMasterOtherModal = true;
      this.masterOtherModalLoading = true;
      this.masterOtherModalRows = [];
      this.masterOtherModalError = '';
      try {
        const result = await this.$apollo.query({
          query: TASK_BOARDS_MASTER_BREAKDOWN_AFTER_TOP,
          variables: {
            scope: this.getBoardsOverviewScope(),
            metric: this.masterOtherModalMetric,
            top_n: 5,
          },
          fetchPolicy: 'network-only',
        });
        const d = result.data && result.data.taskBoardsMasterBoardBreakdownAfterTop;
        if (d && d.error) {
          this.masterOtherModalError = d.message ? String(d.message) : 'Could not load breakdown.';
          return;
        }
        if (d && d.rows && Array.isArray(d.rows)) {
          this.masterOtherModalRows = d.rows;
        }
      } catch (e) {
        this.masterOtherModalError = 'Could not load breakdown.';
      } finally {
        this.masterOtherModalLoading = false;
      }
    },
    closeMasterOtherModal() {
      this.showMasterOtherModal = false;
      this.masterOtherModalRows = [];
      this.masterOtherModalLoading = false;
      this.masterOtherModalError = '';
    },
    async loadSubBoardOtherBreakdown() {
      this.showSubBoardOtherModal = true;
      this.subBoardOtherModalLoading = true;
      this.subBoardOtherModalRows = [];
      this.subBoardOtherModalError = '';
      try {
        const result = await this.$apollo.query({
          query: TASK_BOARDS_SUB_BOARD_TASKS_AFTER_TOP,
          variables: {
            scope: this.getBoardsOverviewScope(),
            top_n: 5,
          },
          fetchPolicy: 'network-only',
        });
        const d = result.data && result.data.taskBoardsSubBoardTasksAfterTop;
        if (d && d.error) {
          this.subBoardOtherModalError = d.message ? String(d.message) : 'Could not load breakdown.';
          return;
        }
        if (d && d.rows && Array.isArray(d.rows)) {
          this.subBoardOtherModalRows = d.rows;
        }
      } catch (e) {
        this.subBoardOtherModalError = 'Could not load breakdown.';
      } finally {
        this.subBoardOtherModalLoading = false;
      }
    },
    closeSubBoardOtherModal() {
      this.showSubBoardOtherModal = false;
      this.subBoardOtherModalRows = [];
      this.subBoardOtherModalLoading = false;
      this.subBoardOtherModalError = '';
    },
    scheduleTasksListSearchDebounce() {
      if (this.viewMode !== 'tasks') {
        return;
      }
      if (this.tasksListSearchDebounceTimer) {
        clearTimeout(this.tasksListSearchDebounceTimer);
        this.tasksListSearchDebounceTimer = null;
      }
      var self = this;
      this.tasksListSearchDebounceTimer = setTimeout(function () {
        self.tasksListSearchDebounceTimer = null;
        self.tasksPage = 1;
        self.tasksListSearchDebounced = (self.tasksListSearch || '').trim();
      }, 400);
    },
    setViewMode(mode) {
      if (this.viewMode === mode) {
        return;
      }
      if (mode === 'boards') {
        this.boardsPage = 1;
      }
      this.viewMode = mode;
    },
    canEditBoard(board) {
      if (this.canEditAllBoards) return true;
      if (!board || !board.created_by || !board.created_by.id || !this.currentUserId) return false;
      return parseInt(board.created_by.id) === this.currentUserId;
    },
    openEditBoard(board) {
      this.selectedBoard = board;
      this.showEditBoardModal = true;
    },
    closeEditBoard() {
      this.showEditBoardModal = false;
      this.selectedBoard = null;
    },
    async confirmDeleteBoard(board) {
      if (!board || !board.id) return;

      const name = board.name || 'this board';
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete ${name}. This action can be undone by restoring from DB, but it will be hidden from the portal.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTaskBoard($id: ID!) {
              deleteTaskBoard(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: { id: board.id }
        });

        const payload = data && data.deleteTaskBoard;
        if (payload && payload.error) {
          await this.$swal.fire({ icon: 'error', title: 'Oops...', text: payload.message || 'Failed to delete board.' });
          return;
        }

        await this.$swal.fire({ icon: 'success', title: 'Deleted', text: (payload && payload.message) || 'Board deleted successfully.' });
        this.handleBoardUpdated();
      } catch (e) {
        await this.$swal.fire({ icon: 'error', title: 'Oops...', text: 'Failed to delete board. Please try again.' });
      }
    },
    setActiveTab(tab) {
      if (this.tasksListSearchDebounceTimer) {
        clearTimeout(this.tasksListSearchDebounceTimer);
        this.tasksListSearchDebounceTimer = null;
      }
      this.tasksListSearch = '';
      this.tasksListSearchDebounced = '';
      this.activeTab = tab;
      this.tasksPage = 1;
      this.$nextTick(() => {
        if (this.viewMode === 'tasks') {
          const vars = this.getTasksListRefetchVariables();
          if (this.activeTab === 'my') {
            this.observerRefetchSmartQuery('myTasksAcrossBoards', vars);
          }
          if (this.activeTab === 'all') {
            this.observerRefetchSmartQuery('allTasksAcrossBoards', vars);
          }
          if (this.activeTab === 'viewer') {
            this.observerRefetchSmartQuery('viewerTasksAcrossBoards', vars);
          }
          this.refetchTaskListStatusCounts();
        }
      });
    },
    refetchTaskListStatusCounts() {
      if (this.viewMode !== 'tasks') {
        return;
      }
      const countVars = { search: this.getTasksListSearchGraphqlVar() };
      this.$nextTick(() => {
        if (this.activeTab === 'my') {
          this.observerRefetchSmartQuery('myTasksAcrossBoardsStatusCounts', countVars);
        } else if (this.activeTab === 'all') {
          this.observerRefetchSmartQuery('allTasksAcrossBoardsStatusCounts', countVars);
        } else if (this.activeTab === 'viewer') {
          this.observerRefetchSmartQuery('viewerTasksAcrossBoardsStatusCounts', countVars);
        }
      });
    },
    statusCountFor(filterValue) {
      const c = this.taskListStatusCounts;
      if (filterValue === 'all') {
        return c.all;
      }
      if (filterValue === 'todo') {
        return c.todo;
      }
      if (filterValue === 'in_progress') {
        return c.in_progress;
      }
      if (filterValue === 'paused') {
        return c.paused;
      }
      if (filterValue === 'in_review') {
        return c.in_review;
      }
      if (filterValue === 'done') {
        return c.done;
      }
      return 0;
    },
    setTaskStatusFilter(value) {
      if (this.taskStatusFilter === value) return;
      this.taskStatusFilter = value;
      this.tasksPage = 1;
      this.$nextTick(() => {
        if (this.viewMode !== 'tasks') return;
        const vars = this.getTasksListRefetchVariables();
        if (this.activeTab === 'my') {
          this.observerRefetchSmartQuery('myTasksAcrossBoards', vars);
        } else if (this.activeTab === 'all') {
          this.observerRefetchSmartQuery('allTasksAcrossBoards', vars);
        } else if (this.activeTab === 'viewer') {
          this.observerRefetchSmartQuery('viewerTasksAcrossBoards', vars);
        }
        this.refetchTaskListStatusCounts();
      });
    },
    setBoardsTab(tab) {
      this.boardsTab = tab;
      this.boardsPage = 1;
      this.$nextTick(() => {
        if (this.canViewAllBoards && this.boardsTab === 'all' && this.$apollo.queries.allTaskMasterBoards) {
          this.$apollo.queries.allTaskMasterBoards.refetch({ first: this.boardsFirst, page: 1 });
        }
        if (this.boardsTab === 'my' && this.$apollo.queries.taskMasterBoards) {
          this.$apollo.queries.taskMasterBoards.refetch({ first: this.boardsFirst, page: 1 });
        }
        this.refetchBoardsOverview();
      });
    },
    getSubBoards(masterBoard) {
      if (!masterBoard) return [];
      const sub = masterBoard.sub_boards;
      return Array.isArray(sub) ? sub : [];
    },
    /** Two-digit index for sub-board list column (distinct from master board disc badge). */
    formatBoardOrdinal(n) {
      var num = parseInt(n, 10);
      if (isNaN(num) || num < 1) {
        return '00';
      }
      if (num < 10) {
        return '0' + num;
      }
      return String(num);
    },
    canEditMasterBoard(masterBoard) {
      if (!masterBoard || parseInt(masterBoard.id) === 0) return false;
      if (this.canEditAllBoards) return true;
      if (!masterBoard.created_by || !masterBoard.created_by.id || !this.currentUserId) return false;
      return parseInt(masterBoard.created_by.id) === this.currentUserId;
    },
    openEditMasterBoard(masterBoard) {
      this.selectedMasterBoard = masterBoard;
      this.showEditMasterBoardModal = true;
    },
    closeEditMasterBoard() {
      this.showEditMasterBoardModal = false;
      this.selectedMasterBoard = null;
    },
    async confirmDeleteMasterBoard(masterBoard) {
      if (!masterBoard || !masterBoard.id) return;

      const name = masterBoard.name || 'this master board';
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete ${name}. Sub-boards will be unassigned but not deleted.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTaskMasterBoard($id: ID!) {
              deleteTaskMasterBoard(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: { id: masterBoard.id }
        });

        const payload = data && data.deleteTaskMasterBoard;
        if (payload && payload.error) {
          await this.$swal.fire({ icon: 'error', title: 'Oops...', text: payload.message || 'Failed to delete master board.' });
          return;
        }

        await this.$swal.fire({ icon: 'success', title: 'Deleted', text: (payload && payload.message) || 'Master board deleted successfully.' });
        this.handleMasterBoardUpdated();
      } catch (e) {
        await this.$swal.fire({ icon: 'error', title: 'Oops...', text: 'Failed to delete master board. Please try again.' });
      }
    },
    openCreateBoardForMasterBoard(masterBoard) {
      this.preselectedMasterBoardId = masterBoard && masterBoard.id ? masterBoard.id : null;
      this.preselectedMasterBoardName = masterBoard && masterBoard.name ? masterBoard.name : null;
      this.preselectedMasterBoardMembers = (masterBoard && masterBoard.members && Array.isArray(masterBoard.members))
        ? masterBoard.members : [];
      this.showCreateBoardModal = true;
    },
    closeCreateBoardModal() {
      this.showCreateBoardModal = false;
      this.preselectedMasterBoardId = null;
      this.preselectedMasterBoardName = null;
      this.preselectedMasterBoardMembers = [];
    },
    handleSubBoardAssigned() {
      this.refetchBoardsData();
    },
    bindTasksScrollListener() {
      this.unbindTasksScrollListener();
      const el = document.getElementById('dashboard-scroll-container');
      if (!el) {
        return;
      }
      this._tasksScrollTarget = el;
      this._tasksScrollHandler = this.handleDashboardScroll.bind(this);
      this._tasksScrollTarget.addEventListener('scroll', this._tasksScrollHandler, { passive: true });
    },
    handleDashboardScroll() {
      if (this.viewMode === 'tasks') {
        this.handleTasksScroll();
      } else if (this.viewMode === 'boards') {
        this.handleBoardsScroll();
      }
    },
    unbindTasksScrollListener() {
      if (this._tasksScrollHandler && this._tasksScrollTarget) {
        this._tasksScrollTarget.removeEventListener('scroll', this._tasksScrollHandler);
      }
      this._tasksScrollHandler = null;
      this._tasksScrollTarget = null;
    },
    handleTasksScroll() {
      if (this.viewMode !== 'tasks') {
        return;
      }
      const info = this.tasksPaginatorInfo;
      if (!info || !info.hasMorePages || this.tasksLoadingMore) {
        return;
      }
      if (!this.displayTasks || this.displayTasks.length === 0) {
        return;
      }
      const root = this._tasksScrollTarget;
      if (!root) {
        return;
      }
      const bottomGap = root.scrollHeight - root.scrollTop - root.clientHeight;
      if (bottomGap >= 200) {
        return;
      }
      this.loadMoreTasks();
    },
    handleBoardsScroll() {
      if (this.viewMode !== 'boards') {
        return;
      }
      const info = this.boardsPaginatorInfo;
      if (!info || !info.hasMorePages || this.boardsLoadingMore) {
        return;
      }
      if (!this.masterBoards || this.masterBoards.length === 0) {
        return;
      }
      const root = this._tasksScrollTarget;
      if (!root) {
        return;
      }
      const bottomGap = root.scrollHeight - root.scrollTop - root.clientHeight;
      if (bottomGap >= 200) {
        return;
      }
      this.loadMoreBoards();
    },
    async loadMoreBoards() {
      const info = this.boardsPaginatorInfo;
      if (!info || !info.hasMorePages || this.boardsLoadingMore) {
        return;
      }
      const nextPage = this.boardsPage + 1;
      const isAll = this.canViewAllBoards && this.boardsTab === 'all';
      const query = isAll ? this.$apollo.queries.allTaskMasterBoards : this.$apollo.queries.taskMasterBoards;
      const queryName = isAll ? 'allTaskMasterBoards' : 'taskMasterBoards';
      if (!query) {
        return;
      }
      this.boardsLoadingMore = true;
      try {
        await query.fetchMore({
          variables: {
            first: this.boardsFirst,
            page: nextPage,
          },
          updateQuery: function (prev, options) {
            var fetchMoreResult = options.fetchMoreResult;
            if (!fetchMoreResult || !fetchMoreResult[queryName]) {
              return prev;
            }
            var prevBlock = prev[queryName];
            var prevData = prevBlock && prevBlock.data ? prevBlock.data : [];
            var newDataRaw =
              fetchMoreResult[queryName] && fetchMoreResult[queryName].data
                ? fetchMoreResult[queryName].data
                : [];
            var hasUnassigned = false;
            for (var i = 0; i < prevData.length; i++) {
              if (parseInt(prevData[i].id) === 0) {
                hasUnassigned = true;
                break;
              }
            }
            var newData = newDataRaw;
            if (hasUnassigned) {
              newData = newDataRaw.filter(function (mb) {
                return parseInt(mb.id) !== 0;
              });
            }
            return {
              [queryName]: Object.assign({}, fetchMoreResult[queryName], {
                data: prevData.concat(newData),
              }),
            };
          },
        });
        this.boardsPage = nextPage;
      } catch (e) {
        console.error('Error loading more boards:', e);
      } finally {
        this.boardsLoadingMore = false;
      }
    },
    async loadMoreTasks() {
      const info = this.tasksPaginatorInfo;
      if (!info || !info.hasMorePages || this.tasksLoadingMore) {
        return;
      }
      const nextPage = this.tasksPage + 1;
      let query;
      let queryName;
      if (this.activeTab === 'my') {
        query = this.$apollo.queries.myTasksAcrossBoards;
        queryName = 'myTasksAcrossBoards';
      } else if (this.activeTab === 'viewer') {
        query = this.$apollo.queries.viewerTasksAcrossBoards;
        queryName = 'viewerTasksAcrossBoards';
      } else {
        query = this.$apollo.queries.allTasksAcrossBoards;
        queryName = 'allTasksAcrossBoards';
      }
      if (!query) {
        return;
      }
      this.tasksLoadingMore = true;
      try {
        await query.fetchMore({
          variables: {
            first: this.tasksFirst,
            page: nextPage,
            status: this.taskStatusFilter === 'all' ? null : this.taskStatusFilter,
            search: this.getTasksListSearchGraphqlVar(),
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult || !fetchMoreResult[queryName]) return prev;
            const prevData = prev[queryName] && prev[queryName].data ? prev[queryName].data : [];
            const newData = fetchMoreResult[queryName].data || [];
            return {
              [queryName]: {
                ...fetchMoreResult[queryName],
                data: prevData.concat(newData),
              },
            };
          },
        });
        this.tasksPage = nextPage;
      } catch (e) {
        console.error('Error loading more tasks:', e);
      } finally {
        this.tasksLoadingMore = false;
      }
    },
    openTaskDetail(taskId) {
      const id = parseInt(taskId, 10);
      if (!id || isNaN(id)) {
        return;
      }
      this.selectedTaskId = id;
    },
    openTaskInNewTab(taskId) {
      if (!taskId) {
        return;
      }
      this.openBoardRouteInNewTab({ path: '/tasks/task/' + taskId });
    },
    refetchTasksList() {
      const vars = this.getTasksListRefetchVariables();
      if (this.activeTab === 'my') {
        this.observerRefetchSmartQuery('myTasksAcrossBoards', vars);
      } else if (this.activeTab === 'viewer') {
        this.observerRefetchSmartQuery('viewerTasksAcrossBoards', vars);
      } else {
        this.observerRefetchSmartQuery('allTasksAcrossBoards', vars);
      }
      this.refetchTaskListStatusCounts();
    },
    handleTaskUpdated() {
      this.refetchTasksList();
    },
    handleTaskDeleted() {
      this.selectedTaskId = null;
      this.refetchTasksList();
    },
    getPriorityColor(priority) {
      const colors = {
        low: '#10B981',
        medium: '#F59E0B',
        high: '#EF4444',
        critical: '#7C3AED',
      };
      return colors[(priority || 'medium').toLowerCase()] || colors.medium;
    },
    getStatusLabel(status) {
      const labels = {
        todo: 'To Do',
        in_progress: 'In Progress',
        paused: 'Paused',
        in_review: 'In Review',
        done: 'Done',
      };
      return labels[(status || 'todo').toLowerCase()] || status;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getTaskStats(board) {
      var defaults = {
        total: 0,
        todo: 0,
        in_progress: 0,
        paused: 0,
        in_review: 0,
        done: 0,
      };
      if (!board || !board.task_stats) {
        return defaults;
      }
      var s = board.task_stats;
      var statInt = function (v, fallback) {
        if (typeof v === 'number' && !isNaN(v)) {
          return v;
        }
        if (typeof v === 'string' && v !== '' && !isNaN(Number(v))) {
          return parseInt(v, 10);
        }
        return fallback;
      };
      // Merge so partial GraphQL/cache objects still show counts; coerce strings (some clients serialize Int as string).
      return {
        total: statInt(s.total, defaults.total),
        todo: statInt(s.todo, defaults.todo),
        in_progress: statInt(s.in_progress, defaults.in_progress),
        paused: statInt(s.paused, defaults.paused),
        in_review: statInt(s.in_review, defaults.in_review),
        done: statInt(s.done, defaults.done),
      };
    },
    getMembersCount(board) {
      if (!board || !board.members || !Array.isArray(board.members)) {
        return 0;
      }
      return board.members.length;
    },
    openBoard(boardId) {
      this.$router.push(`/tasks/board/${boardId}`);
    },
    openBoardRouteInNewTab(location) {
      var resolved = this.$router.resolve(location);
      var url = resolved.href;
      if (!url || url.indexOf('http') !== 0) {
        var fp = resolved.fullPath || '';
        if (fp.indexOf('/') !== 0) {
          fp = '/' + fp;
        }
        url =
          window.location.origin +
          (window.location.pathname || '/') +
          (window.location.search || '') +
          '#' +
          fp;
      }
      // Synthetic <a click> often ignores target="_blank"; window.open runs in the user gesture stack.
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    goToTaskSubBoard(task) {
      if (!task || !task.board || !task.board.id) {
        return;
      }
      this.openBoardRouteInNewTab({ path: '/tasks/board/' + task.board.id });
    },
    goToTaskMasterBoard(task) {
      if (!task || !task.board || !task.board.master_board || !task.board.master_board.id) {
        return;
      }
      this.openBoardRouteInNewTab({ path: '/tasks/master-board/' + task.board.master_board.id });
    },
    openMasterBoard(masterBoardId) {
      if (masterBoardId) {
        this.$router.push(`/tasks/master-board/${masterBoardId}`);
      }
    },
    openBoardMembersModal(board, kind) {
      this.selectedBoardForMembers = board;
      this.membersModalKind = kind === 'master' ? 'master' : 'sub';
    },
    closeBoardMembersModal() {
      this.selectedBoardForMembers = null;
      this.membersModalKind = 'sub';
    },
    getProfilePicture(url) {
      if (!url) {
        return '/img/default-avatar.png';
      }
      if (url.indexOf('http') === 0) {
        return url;
      }
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    handleImageError(event) {
      event.target.src = '/img/default-avatar.png';
    },
    goToProfile(member) {
      if (member && member.token) {
        this.openBoardRouteInNewTab({ path: '/profile/' + member.token });
      }
    },
    isCreator(member, board) {
      if (!board || !board.created_by || !member) {
        return false;
      }
      return parseInt(member.id) === parseInt(board.created_by.id);
    },
    handleBoardCreated() {
      this.closeCreateBoardModal();
      this.refetchBoardsData();
    },
    handleMasterBoardCreated() {
      this.showCreateMasterBoardModal = false;
      this.refetchBoardsData();
    },
    closeCreateTaskModal() {
      this.showCreateTaskModal = false;
      this.createTaskModalLayout = 1;
    },
    handleTaskCreated() {
      this.showCreateTaskModal = false;
      this.createTaskModalLayout = 1;
      const vars = this.getTasksListRefetchVariables();
      this.observerRefetchSmartQuery('myTasksAcrossBoards', vars);
      this.observerRefetchSmartQuery('allTasksAcrossBoards', vars);
      this.observerRefetchSmartQuery('viewerTasksAcrossBoards', vars);
      this.refetchTaskListStatusCounts();
    },
    handleBoardUpdated() {
      this.closeEditBoard();
      this.refetchBoardsData();
    },
    handleMasterBoardUpdated() {
      this.closeEditMasterBoard();
      this.refetchBoardsData();
    },
    refetchBoardsData() {
      this.boardsPage = 1;
      if (this.$apollo.queries.taskMasterBoards) {
        this.$apollo.queries.taskMasterBoards.refetch({ first: this.boardsFirst, page: 1 });
      }
      if (this.canViewAllBoards && this.$apollo.queries.allTaskMasterBoards) {
        this.$apollo.queries.allTaskMasterBoards.refetch({ first: this.boardsFirst, page: 1 });
      }
      if (this.viewMode === 'boards') {
        this.refetchBoardsOverview();
      }
      if (this.viewMode === 'tasks') {
        const vars = this.getTasksListRefetchVariables();
        this.observerRefetchSmartQuery('myTasksAcrossBoards', vars);
        this.observerRefetchSmartQuery('allTasksAcrossBoards', vars);
        this.observerRefetchSmartQuery('viewerTasksAcrossBoards', vars);
        this.refetchTaskListStatusCounts();
      }
    },
  },
};
</script>

<style scoped>
.tasks-page {
  min-height: calc(100vh - 80px);
}

.task-card-index {
  min-width: 2.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
</style>
